import CareEvidenceReviewPanel from '../../../features/sourceLifecycle/CareEvidenceReviewPanel';
import React, { useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  Snackbar,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import PlaylistAddCheckRoundedIcon from "@mui/icons-material/PlaylistAddCheckRounded";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import PsychologyAltOutlinedIcon from "@mui/icons-material/PsychologyAltOutlined";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import colors from "../../../assets/theme/colors";
import { buildCareProfileGuidance } from "../../../utils/careProfileGuidance";
import {
  CARE_PROFILE_SECTION_IDS,
  getCareProfileOverviewCompletionSummary,
  getCareProfileOverviewSections,
} from "../../../utils/careProfileSections";
import {
  CareProfileSectionCard,
  NeedToKnowCard,
  SECTION_ICON_BY_ID,
} from "./CareProfileOverviewCards";
import CareProfileSectionNavigator from "./CareProfileSectionNavigator";
import CareProfileStoryShareSheet from "./CareProfileStoryShareSheet";
import CareProfileStoryReview from "./CareProfileStoryReview";
import UnifiedProfileShareFlow from "../../../features/profileSharing/UnifiedProfileShareFlow";
import { ProfileSetupIdentity } from "./ProfileSetupIndicator";
import ProfileCommunicationChapter from "./ProfileCommunicationChapter";
import ProfileWhatHelpsChapter from "./ProfileWhatHelpsChapter";
import ProfileWatchForChapter from "./ProfileWatchForChapter";
import ProfileDailyLifeChapter from "./ProfileDailyLifeChapter";
import ProfileSafetyChapter from "./ProfileSafetyChapter";
import ProfileHealthCareChapter from "./ProfileHealthCareChapter";
import ProfileGoalsChapter from "./ProfileGoalsChapter";
import {
  CARE_SETUP_SECTION_IDS,
  CaregiverHandbookHeading,
  CareSetupSection,
  CollectedInformationCard,
  getCareSetupDestinations,
  LandingNavCard,
  QuickAccessRow,
  RecentUpdatesSection,
  TransitionalProfileSetupHeading,
} from "./CareProfileLandingSections";
import UniversalProfileAddSheet from "./UniversalProfileAddSheet";
import {
  PROFILE_KNOWLEDGE_CHAPTERS,
  PROFILE_PLACEMENT_MODES,
  getProfilePlacement,
} from "../../../features/profileContract";
import { CARE_SNAPSHOT_STEPS } from "../../../constants/careSnapshotSections";
import { buildProfileLandingNeedToKnowItems } from "./profileLandingNeedToKnow";
import ProfileReviewQueue from "../../../features/careSourceProcessing/ProfileReviewQueue";
import ProfileGoalSheet from "./ProfileGoalSheet";
import useProfileGoalManagement from "./useProfileGoalManagement";

const SECTION_GROUPS = [
  {
    title: "Core Info",
    ids: [
      CARE_PROFILE_SECTION_IDS.ABOUT,
      CARE_PROFILE_SECTION_IDS.MEDICATION,
      CARE_PROFILE_SECTION_IDS.ALLERGIES_FOOD,
      CARE_PROFILE_SECTION_IDS.SYMPTOMS_MONITORING,
    ],
  },
  {
    title: "Support Guides",
    ids: [
      CARE_PROFILE_SECTION_IDS.DAILY_SUPPORT,
      CARE_PROFILE_SECTION_IDS.BEHAVIOR_SUPPORT,
      CARE_PROFILE_SECTION_IDS.SENSORY_PROFILE,
      CARE_PROFILE_SECTION_IDS.COMMUNICATION,
      CARE_PROFILE_SECTION_IDS.MEMORY_ORIENTATION,
    ],
  },
  {
    title: "Planning & Team",
    ids: [
      CARE_PROFILE_SECTION_IDS.ROUTINE_TRANSITIONS,
      CARE_PROFILE_SECTION_IDS.GOALS,
      CARE_PROFILE_SECTION_IDS.CARE_TEAM,
      CARE_PROFILE_SECTION_IDS.MOBILITY_SAFETY,
      CARE_PROFILE_SECTION_IDS.SLEEP,
      CARE_PROFILE_SECTION_IDS.EMERGENCY_PROTOCOL,
    ],
  },
];

const formatUpdateTimeLabel = (isoString) => {
  const date = isoString ? new Date(isoString) : null;
  if (!date || Number.isNaN(date.getTime())) return "";

  const now = new Date();
  const isSameDay = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();
  const time = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  if (isSameDay) return `Today, ${time}`;
  if (isYesterday) return `Yesterday, ${time}`;
  return date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
};

// Index rows communicate title + status only - never the full caregiver
// question or chapter summary (that belongs to the chapter's own reading surface).
const getHandbookChapterStatusLabel = (chapter) => {
  const briefingCount = Array.isArray(chapter?.briefing?.items) ? chapter.briefing.items.length : 0;
  const handbookEntryCount = Array.isArray(chapter?.handbook?.groups)
    ? chapter.handbook.groups.reduce((total, group) => total + (group.entries?.length || 0), 0)
    : 0;
  const count = handbookEntryCount || briefingCount;
  if (!count) return "Needs information";
  return `${count} guidance item${count === 1 ? "" : "s"}`;
};

// Handbook table-of-contents rows. "glance" has no domain builder/chapter object
// yet (Care Story fills that role today) so it renders the existing StoryPreviewCard
// instead of a Profile*Chapter component - reusing existing content, not new copy.
const PROFILE_CHAPTER_ICON_BY_VIEW_ID = {
  communication: ChatBubbleOutlineRoundedIcon,
  whatHelps: FavoriteBorderRoundedIcon,
  watchFor: VisibilityOutlinedIcon,
  dailyLife: WbSunnyOutlinedIcon,
  safety: ShieldOutlinedIcon,
  healthCare: MedicationOutlinedIcon,
};

const HANDBOOK_CHAPTERS = [
  { id: "glance", icon: AutoAwesomeRoundedIcon, fallbackTitle: "At a Glance" },
  ...PROFILE_KNOWLEDGE_CHAPTERS.map((chapter) => ({
    id: chapter.profileViewId,
    icon: PROFILE_CHAPTER_ICON_BY_VIEW_ID[chapter.profileViewId],
    fallbackTitle: chapter.label,
  })),
  { id: "goals", icon: FlagOutlinedIcon, fallbackTitle: "Goals" },
];

const NEXT_SECTION_ICON_BY_ID = {
  [CARE_PROFILE_SECTION_IDS.ABOUT]: DescriptionOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.MEDICATION]: MedicationOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.ALLERGIES_FOOD]: RestaurantMenuOutlinedIcon,
  [CARE_PROFILE_SECTION_IDS.DAILY_SUPPORT]: FavoriteBorderRoundedIcon,
  [CARE_PROFILE_SECTION_IDS.BEHAVIOR_SUPPORT]: PsychologyAltOutlinedIcon,
};

const buildSectionGroups = (profileOverview = {}, excludedSectionIds = []) => {
  const sections = getCareProfileOverviewSections(profileOverview);
  const excludedIds = new Set(excludedSectionIds);
  const sectionsById = new Map(
    sections
      .filter((section) => !excludedIds.has(section.id))
      .map((section) => [section.id, section])
  );
  const usedIds = new Set();

  const groups = SECTION_GROUPS
    .map((group) => {
      const items = group.ids
        .map((id) => sectionsById.get(id))
        .filter(Boolean);
      items.forEach((section) => usedIds.add(section.id));
      return { ...group, sections: items };
    })
    .filter((group) => group.sections.length);

  const remaining = sections.filter((section) => !excludedIds.has(section.id) && !usedIds.has(section.id));
  return remaining.length
    ? [...groups, { title: "More Details", sections: remaining }]
    : groups;
};

const getStoryPreviewText = (story = {}) => {
  const summaryText = Array.isArray(story.summary)
    ? story.summary.find((item) => String(item || "").trim())
    : "";
  const fallbackText = story.chapters
    ?.flatMap((chapter) => chapter.paragraphs || [])
    ?.find((item) => String(item || "").trim());
  const text = String(summaryText || fallbackText || "The Care Story will grow as the profile fills in.")
    .replace(/\s+/g, " ")
    .trim();

  if (text.length <= 265) return text;
  return `${text.slice(0, 262).trim()}...`;
};

export const getCareStoryStatus = (profileSetupStatus = {}) => {
  if (profileSetupStatus?.isComplete) {
    return {
      label: "Ready",
      bgcolor: colors.brand.deep,
      color: colors.semantic.profileFlow.cardBg,
    };
  }

  const complete = Number(profileSetupStatus?.completionSummary?.complete || 0);
  return {
    label: complete > 0 ? "Building" : "Not ready yet",
    bgcolor: alpha(colors.brand.deep, 0.12),
    color: colors.brand.deep,
  };
};

export const buildProfileChapterAddContext = (chapterId, presentation = {}) => {
  const placement = getProfilePlacement(chapterId);
  if (!placement) return null;
  return {
    initialIntent: "knowledge",
    ...presentation,
    profileChapterId: placement.chapterId,
    profileField: placement.profileField || "",
    profilePlacementMode: PROFILE_PLACEMENT_MODES.EXPLICIT_CAREGIVER,
  };
};

const ProfileHero = ({
  child,
  childAccent,
  displayName,
  age,
  photoURL,
  conditionLabel,
  profileSetupStatus,
  preferredLanguage,
  showProgress = true,
  onEdit,
}) => {
  const identityDetails = [
    age ? `${age} years old` : null,
    preferredLanguage ? `Speaks ${preferredLanguage}` : null,
  ].filter(Boolean);
  const careFocusLabel = conditionLabel ? conditionLabel.replace(/^\s*·\s*/, "") : "";
  const isEditable = typeof onEdit === "function";

  return (
  <Box
    sx={{
      px: { xs: 0.25, sm: 0.5 },
      py: { xs: 0.35, sm: 0.55 },
      bgcolor: colors.semantic.profileFlow.cardBg,
    }}
  >
    <Stack spacing={0.6}>
      <Box
        component={isEditable ? ButtonBase : "div"}
        onClick={isEditable ? onEdit : undefined}
        aria-label={isEditable ? `Edit ${displayName}'s profile` : undefined}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          textAlign: "left",
          borderRadius: "20px",
          position: "relative",
        }}
      >
        <ProfileSetupIdentity
          child={{ ...(child || {}), name: displayName, photoURL }}
          setupStatus={profileSetupStatus}
          title={displayName}
          subtitle={identityDetails.length ? identityDetails.join(" · ") : ""}
          avatarSize={74}
          ringWidth={4}
          accentColor={childAccent?.primary}
          showPercentBadge={showProgress}
          showCompletionLabel={false}
          sx={{ gap: { xs: 1, sm: 1.25 }, width: "100%" }}
          avatarContainerSx={{ width: 88, height: 88 }}
          contentSx={{ flex: 1 }}
          titleSx={{ fontWeight: 800, fontSize: { xs: "1.55rem", sm: "1.82rem" }, lineHeight: 1.05, display: "inline-flex", alignItems: "center", gap: 0.6 }}
          subtitleSx={{ mt: 0.28, fontSize: { xs: "0.84rem", sm: "0.92rem" } }}
        />
        {isEditable ? (
          <EditRoundedIcon
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: { xs: 4, sm: 8 },
              left: { xs: 88, sm: 96 },
              fontSize: 18,
              color: colors.semantic.ink2,
            }}
          />
        ) : null}
        {isEditable ? (
          <Box
            sx={{
              position: "absolute",
              left: { xs: 58, sm: 66 },
              top: { xs: 58, sm: 66 },
              width: 26,
              height: 26,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: colors.semantic.profileFlow.cardBg,
              border: `1px solid ${colors.semantic.line2}`,
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
            }}
          >
            <PhotoCameraRoundedIcon sx={{ fontSize: 14, color: colors.semantic.ink2 }} />
          </Box>
        ) : null}
      </Box>
      {careFocusLabel ? (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pl: { xs: 0.25, sm: 0.5 } }}>
          <ExploreOutlinedIcon aria-hidden="true" sx={{ fontSize: 17, color: colors.brand.deep }} />
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 500, color: colors.brand.deep }}>
            {careFocusLabel}
          </Typography>
        </Stack>
      ) : null}
      {isEditable ? (
        <Typography
          sx={{
            pl: { xs: 0.25, sm: 0.5 },
            fontSize: "0.7rem",
            fontWeight: 500,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: colors.semantic.ink2,
          }}
        >
          Tap the header above to edit
        </Typography>
      ) : null}
    </Stack>
  </Box>
  );
};

const NextSectionCard = ({
  guidance,
  onSectionSelect,
  onStepChange,
}) => {
  const nextSection = guidance?.actions?.[0] || null;
  if (!nextSection) return null;
  const NextIcon = NEXT_SECTION_ICON_BY_ID[nextSection.id] || AutoAwesomeRoundedIcon;
  const actionLabel = nextSection.actionLabel || "Add";

  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 1.45 },
        borderRadius: "24px",
        border: `2px solid ${colors.semantic.profileFlow.healthIconBg}`,
        bgcolor: alpha(colors.semantic.profileFlow.healthIconBg, 0.18),
      }}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", gap: 1, alignItems: "center" }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "16px",
            display: "grid",
            placeItems: "center",
            bgcolor: colors.semantic.profileFlow.healthIconBg,
            color: colors.semantic.profileFlow.healthIconInk,
          }}
        >
          <NextIcon sx={{ fontSize: 21 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: "0.96rem", sm: "1.05rem" }, fontWeight: 500, color: colors.semantic.profileFlow.healthIconInk, lineHeight: 1.15 }}>
            {nextSection.actionLabel} next
          </Typography>
          <Typography sx={{ mt: 0.2, fontSize: { xs: "0.86rem", sm: "0.94rem" }, color: alpha(colors.semantic.profileFlow.healthIconInk, 0.78), lineHeight: 1.35 }}>
            {nextSection.guidanceReason || nextSection.description}
          </Typography>
        </Box>
        <Button
          variant="contained"
          aria-label={actionLabel}
          onClick={() => {
            if (onSectionSelect) {
              onSectionSelect(nextSection);
              return;
            }
            onStepChange?.(nextSection.targetStep);
          }}
          sx={{
            minWidth: 68,
            borderRadius: "16px",
            px: 1.2,
            py: 0.8,
            textTransform: "none",
            fontSize: "0.92rem",
            fontWeight: 500,
            bgcolor: colors.semantic.profileFlow.healthIconInk,
            color: colors.semantic.profileFlow.cardBg,
            boxShadow: "none",
            "&:hover": { bgcolor: alpha(colors.semantic.profileFlow.healthIconInk, 0.9), boxShadow: "none" },
          }}
        >
          {actionLabel}
        </Button>
      </Box>
    </Box>
  );
};

const StoryPreviewCard = ({
  story,
  onReadFullStory,
  onShare,
  profileSetupStatus,
  variant = "preview",
}) => {
  // "atAGlance" is the Handbook's At a Glance reading surface: it *is* the full
  // story already, so a "Read full Care Story" link back to itself would be a
  // dead action pointing at nothing.
  const isFullReadingSurface = variant === "atAGlance";
  const [shareOpen, setShareOpen] = useState(false);
  if (!story) return null;
  const preview = getStoryPreviewText(story);
  const storyStatus = getCareStoryStatus(profileSetupStatus);

  return (
    <>
      <Box
        sx={{
          p: { xs: 1.35, sm: 1.65 },
          borderRadius: "28px",
          border: `2px solid ${alpha(colors.brand.deep, 0.25)}`,
          bgcolor: alpha(colors.brand.ice, 0.92),
        }}
      >
        <Stack spacing={1.45}>
          <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto", gap: 1.1, alignItems: "start" }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "18px",
                display: "grid",
                placeItems: "center",
                bgcolor: colors.brand.deep,
                color: colors.semantic.profileFlow.cardBg,
              }}
            >
              <AutoAwesomeRoundedIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: { xs: "1.18rem", sm: "1.35rem" }, fontWeight: 500, color: colors.brand.deep, lineHeight: 1.1 }}>
                {story.title}
              </Typography>
              <Typography sx={{ mt: 0.25, fontSize: { xs: "0.92rem", sm: "1rem" }, color: alpha(colors.brand.deep, 0.72), lineHeight: 1.35 }}>
                Generated from the profile · updates automatically
              </Typography>
            </Box>
            <Box
              sx={{
                px: 1.1,
                py: 0.35,
                borderRadius: 999,
                bgcolor: storyStatus.bgcolor,
                color: storyStatus.color,
                fontSize: "0.75rem",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {storyStatus.label}
            </Box>
          </Box>

          <Box sx={{ p: { xs: 1.45, sm: 1.75 }, borderRadius: "24px", bgcolor: alpha(colors.semantic.profileFlow.cardBg, 0.82) }}>
            <Typography sx={{ fontSize: { xs: "1.05rem", sm: "1.18rem" }, lineHeight: 1.72, color: colors.brand.deep, fontStyle: "italic" }}>
              "{preview}"
            </Typography>
            {isFullReadingSurface ? null : (
              <Button
                variant="text"
                onClick={onReadFullStory}
                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 21 }} />}
                sx={{
                  mt: 0.9,
                  px: 0,
                  textTransform: "none",
                  fontSize: "0.98rem",
                  fontWeight: 500,
                  color: colors.brand.deep,
                  "&:hover": { bgcolor: "transparent", color: colors.brand.deep },
                }}
              >
                Read full Care Story
              </Button>
            )}
          </Box>

          <Button
            variant="contained"
            startIcon={<IosShareRoundedIcon sx={{ fontSize: 22 }} />}
            onClick={() => (onShare ? onShare() : setShareOpen(true))}
            sx={{
              minHeight: 58,
              borderRadius: "22px",
              textTransform: "none",
              fontSize: { xs: "1rem", sm: "1.08rem" },
              fontWeight: 500,
              bgcolor: colors.brand.deep,
              color: colors.semantic.profileFlow.cardBg,
              boxShadow: "none",
              "&:hover": { bgcolor: alpha(colors.brand.deep, 0.92), boxShadow: "none" },
            }}
          >
            Share with a caregiver
          </Button>
          <Typography sx={{ px: 0.5, fontSize: "0.88rem", color: alpha(colors.brand.deep, 0.64), textAlign: "center" }}>
            Creates a one-time snapshot of the sections you choose.
          </Typography>
        </Stack>
      </Box>
      {onShare ? null : (
        <CareProfileStoryShareSheet
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          story={story}
        />
      )}
    </>
  );
};

const SectionGroup = ({ title, sections, onSectionSelect, onStepChange, onOpenCareTeam }) => (
  <Stack spacing={0.8}>
    <Typography sx={{ pl: 0.25, fontSize: "0.82rem", fontWeight: 500, letterSpacing: "0.14em", color: colors.semantic.profileFlow.eyebrowText, textTransform: "uppercase" }}>
      {title}
    </Typography>
    <Stack spacing={0.85}>
      {sections.map((section) => (
        <CareProfileSectionCard
          key={section.id}
          section={section}
          onSectionSelect={onSectionSelect}
          onStepChange={onStepChange}
          onOpenCareTeam={onOpenCareTeam}
        />
      ))}
    </Stack>
  </Stack>
);

const StarterLoggingHandoff = ({ displayName }) => (
  <Box
    sx={{
      p: 1.4,
      borderRadius: 2.2,
      border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
      bgcolor: colors.semantic.profileFlow.cardBg,
    }}
  >
    <Typography sx={{ fontSize: "1.05rem", fontWeight: 500, color: colors.landing.heroText }}>
      Ready to start logging
    </Typography>
    <Typography sx={{ mt: 0.2, fontSize: "0.82rem", color: colors.semantic.ink2 }}>
      Once {displayName}'s name, date of birth, and care focus are saved, you can start adding timeline notes. Medications, routines, supports, and care team details can come later.
    </Typography>
  </Box>
);

const AddModeActions = ({
  stage,
  isSubmitting,
  onCreate,
  onFinish,
}) => {
  const primaryLabel = stage === "setup" ? "Start Logging" : "Create & Start Logging";
  const primaryAction = stage === "setup" ? onFinish : onCreate;
  const primaryDisabled = isSubmitting || typeof primaryAction !== "function";

  return (
    <Stack spacing={1} sx={{ pt: 0.5 }}>
      <Button
        variant="contained"
        onClick={() => primaryAction?.()}
        disabled={primaryDisabled}
        fullWidth
        size="large"
        sx={{
          minHeight: 52,
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 500,
          bgcolor: colors.semantic.profileFlow.completionText,
          "&:hover": { bgcolor: colors.brand.navy },
        }}
      >
        {primaryLabel}
      </Button>
    </Stack>
  );
};

const CareProfileOverview = ({
  child,
  childAccent,
  profileName,
  displayName,
  age,
  photoURL,
  selectedConditions = [],
  careContext = {},
  profileOverview,
  profileStory,
  communicationChapter,
  communicationChapterStatus = "ready",
  knowledgeFacts = [],
  whatHelpsChapter,
  whatHelpsChapterStatus = "ready",
  watchForChapter,
  watchForChapterStatus = "ready",
  dailyLifeChapter,
  dailyLifeChapterStatus = "ready",
  safetyChapter,
  safetyChapterStatus = "ready",
  healthCareChapter,
  healthCareChapterStatus = "ready",
  goalsChapter,
  goalsChapterStatus = "ready",
  medications = [],
  providers = [],
  foodAllergies = [],
  dietaryRestrictions = [],
  documents = {},
  stage,
  mode,
  isSubmitting,
  onSectionSelect,
  onStepChange,
  onCreate,
  onFinish,
  onOpenCareTeam,
  onOpenProviders,
  onOpenCollectedInformation,
  onOpenPlans,
  onAddIntent,
  canEditProfile = true,
  canViewDocuments = false,
  profileSetupStatus = null,
  landingView = null,
  onLandingViewChange,
  handbookChapterId = null,
  onHandbookChapterIdChange,
  handbookChapterQuickEntry = false,
  onHandbookChapterQuickEntryChange,
}) => {
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);
  const [addSheetContext, setAddSheetContext] = useState({});
  const [isShareOpen, setIsShareOpen] = useState(false);
  const {
    childForGoalWrites,
    resolvedGoalsChapter,
    selectedGoal,
    closeGoal,
    openGoal,
    applySupportProfile,
    handleRemoved,
    message: goalMessage,
    clearMessage: clearGoalMessage,
    canUndoRemove,
    undoRemove,
  } = useProfileGoalManagement({ child, goalsChapter, canEditProfile });
  // Uncontrolled fallback keeps this component self-contained wherever a parent
  // doesn't need to preserve drill-in state across navigation (e.g. tests).
  // EditChildModal controls this so Back/Save from Care Setup destinations can
  // return to the Care Setup list instead of the root Profile landing.
  const [internalLandingView, setInternalLandingView] = useState(null);
  const activeLandingView = onLandingViewChange ? landingView : internalLandingView;
  const setActiveLandingView = onLandingViewChange || setInternalLandingView;
  // Same controlled/uncontrolled hybrid, one level deeper: which Handbook chapter
  // (if any) is open. null = Handbook table of contents.
  const [internalHandbookChapterId, setInternalHandbookChapterId] = useState(null);
  const activeHandbookChapterId = onHandbookChapterIdChange ? handbookChapterId : internalHandbookChapterId;
  const setActiveHandbookChapterId = onHandbookChapterIdChange || setInternalHandbookChapterId;
  // Tracks whether the open chapter was reached by skipping the table of
  // contents (Quick Access jumps straight to a chapter). When true, Back
  // must return straight to the Profile root instead of the unseen TOC.
  const [internalHandbookChapterQuickEntry, setInternalHandbookChapterQuickEntry] = useState(false);
  const activeHandbookChapterQuickEntry = onHandbookChapterQuickEntryChange
    ? handbookChapterQuickEntry
    : internalHandbookChapterQuickEntry;
  const setActiveHandbookChapterQuickEntry = onHandbookChapterQuickEntryChange || setInternalHandbookChapterQuickEntry;
  const conditionLabel = selectedConditions?.[0]?.label ? ` · ${selectedConditions[0].label}` : "";
  const preferredLanguage = child?.preferredLanguage || child?.primaryLanguage || child?.language || "";
  const isStarterOnlyIntake = mode === "add" && stage === "intake";
  const completionSummary = profileSetupStatus?.completionSummary
    || getCareProfileOverviewCompletionSummary(profileOverview);
  const resolvedProfileSetupStatus = profileSetupStatus || {
    completionPercent: completionSummary.percent,
    completionSummary,
    completionLabel: completionSummary.label,
    isComplete: completionSummary.remaining === 0,
    shouldShowRing: completionSummary.remaining > 0,
    shouldShowSingleItemPill: false,
  };
  const guidance = buildCareProfileGuidance({
    mode,
    stage,
    name: profileName,
    age,
    selectedConditions,
    careContext,
    profileOverview,
  });
  // TODO: Remove the development comparison after DM_02 handbook acceptance is complete.
  const showLegacyCommunicationComparison = process.env.NODE_ENV !== "production";
  const sectionGroups = buildSectionGroups(
    profileOverview,
    [
      ...CARE_SETUP_SECTION_IDS,
      ...(communicationChapter && !showLegacyCommunicationComparison
        ? [CARE_PROFILE_SECTION_IDS.COMMUNICATION]
        : []),
    ]
  );
  const profileSections = getCareProfileOverviewSections(profileOverview);
  const legacyProfileSections = profileSections.filter(
    (section) => !CARE_SETUP_SECTION_IDS.includes(section.id)
  );
  const legacyCompletionSummary = getCareProfileOverviewCompletionSummary({
    coreSections: legacyProfileSections,
  });
  const aboutSection = profileSections.find(
    (section) => section.id === CARE_PROFILE_SECTION_IDS.ABOUT
  ) || null;
  const handleEditHeader = aboutSection
    ? () => {
      if (onSectionSelect) {
        onSectionSelect(aboutSection);
        return;
      }
      onStepChange?.(aboutSection.targetStep);
    }
    : undefined;

  const isLanding = !isStarterOnlyIntake && mode === "edit" && !activeLandingView;
  const openStoryView = () => setActiveLandingView("story");

  const CHAPTER_ADD_CTA = {
    communication: {
      label: "Add Communication",
      chapterName: "Communication",
      captureTitle: "Communication guidance",
      capturePlaceholder: "Describe how they express needs, understand language, or make choices...",
    },
    whatHelps: {
      label: "Add What Helps",
      chapterName: "What Helps",
      captureTitle: "What helps them",
      capturePlaceholder: "Describe what helps them feel comfortable, calm, or successful...",
    },
    watchFor: {
      label: "Add Watch For",
      chapterName: "Watch For",
      captureTitle: "Things to watch for",
      capturePlaceholder: "Describe signs, triggers, or behaviors caregivers should watch for...",
    },
    dailyLife: {
      label: "Add Daily Routine",
      chapterName: "Daily Life",
      captureTitle: "Daily routine",
      capturePlaceholder: "Describe a typical day, routines, or preferences...",
    },
    safety: {
      label: "Add Safety Guidance",
      chapterName: "Safety",
      captureTitle: "Safety guidance",
      capturePlaceholder: "Describe safety considerations, precautions, or emergency steps...",
    },
    healthCare: {
      label: "Add Health Info",
      chapterName: "Health & Care",
      captureTitle: "Health and care information",
      capturePlaceholder: "Describe medications, conditions, therapies, or care instructions...",
    },
    goals: {
      label: "Add Goal",
      chapterName: "Goals",
      captureTitle: "Care goals",
      capturePlaceholder: "Describe a goal you're working toward or progress you've made...",
    },
  };

  const openAddSheetForChapter = (chapterId) => {
    const ctx = CHAPTER_ADD_CTA[chapterId];
    const addContext = ctx ? buildProfileChapterAddContext(chapterId, ctx) : null;
    if (!addContext) return;
    setAddSheetContext(addContext);
    setIsAddSheetOpen(true);
  };
  // DM_07 manager-reference entries (medication/allergy) hand off to the
  // existing Health & Care wizard step rather than any Knowledge-fact edit.
  // There is currently no dedicated step/view for Allergies & Food distinct
  // from Medications, so both destinations land on the same Health & Care
  // step today - see the DM_07 report's deferred gaps.
  const handleNavigateToManager = () => {
    onStepChange?.(CARE_SNAPSHOT_STEPS.HEALTH_CARE);
  };
  const chapterPropsById = {
    communication: { chapter: communicationChapter, status: communicationChapterStatus, Component: ProfileCommunicationChapter, extraProps: { knowledgeFacts, canEditProfile, canViewDocuments, onAddCta: canEditProfile ? () => openAddSheetForChapter("communication") : undefined, addCtaLabel: CHAPTER_ADD_CTA.communication.label } },
    whatHelps: { chapter: whatHelpsChapter, status: whatHelpsChapterStatus, Component: ProfileWhatHelpsChapter, extraProps: { knowledgeFacts, canEditProfile, canViewDocuments, onAddCta: canEditProfile ? () => openAddSheetForChapter("whatHelps") : undefined, addCtaLabel: CHAPTER_ADD_CTA.whatHelps.label } },
    watchFor: { chapter: watchForChapter, status: watchForChapterStatus, Component: ProfileWatchForChapter, extraProps: { knowledgeFacts, canEditProfile, canViewDocuments, onAddCta: canEditProfile ? () => openAddSheetForChapter("watchFor") : undefined, addCtaLabel: CHAPTER_ADD_CTA.watchFor.label } },
    dailyLife: { chapter: dailyLifeChapter, status: dailyLifeChapterStatus, Component: ProfileDailyLifeChapter, extraProps: { knowledgeFacts, canEditProfile, canViewDocuments, onAddCta: canEditProfile ? () => openAddSheetForChapter("dailyLife") : undefined, addCtaLabel: CHAPTER_ADD_CTA.dailyLife.label } },
    safety: { chapter: safetyChapter, status: safetyChapterStatus, Component: ProfileSafetyChapter, extraProps: { knowledgeFacts, canEditProfile, canViewDocuments, onAddCta: canEditProfile ? () => openAddSheetForChapter("safety") : undefined, addCtaLabel: CHAPTER_ADD_CTA.safety.label } },
    healthCare: { chapter: healthCareChapter, status: healthCareChapterStatus, Component: ProfileHealthCareChapter, extraProps: { knowledgeFacts, canEditProfile, canViewDocuments, onNavigateToManager: handleNavigateToManager, onAddCta: canEditProfile ? () => openAddSheetForChapter("healthCare") : undefined, addCtaLabel: CHAPTER_ADD_CTA.healthCare.label } },
    goals: { chapter: resolvedGoalsChapter, status: goalsChapterStatus, Component: ProfileGoalsChapter, extraProps: { onEntryAction: canEditProfile ? openGoal : undefined, onAddCta: canEditProfile ? () => openAddSheetForChapter("goals") : undefined, addCtaLabel: CHAPTER_ADD_CTA.goals.label } },
  };
  const activeHandbookChapterMeta = HANDBOOK_CHAPTERS.find((entry) => entry.id === activeHandbookChapterId) || null;
  const shareChapters = [
    { id: "communication", title: "Communication", chapter: communicationChapter },
    { id: "what_helps", title: "What Helps", chapter: whatHelpsChapter },
    { id: "watch_for", title: "Watch For", chapter: watchForChapter },
    { id: "daily_life", title: "Daily Life", chapter: dailyLifeChapter },
    { id: "safety", title: "Safety", chapter: safetyChapter },
    { id: "health_care", title: "Health & Care", chapter: healthCareChapter },
    { id: "goals", title: "Goals", chapter: resolvedGoalsChapter },
  ];

  const careSetupDestinations = getCareSetupDestinations(profileSections, Boolean(onOpenCareTeam));
  const careSetupDestinationsById = new Map(
    careSetupDestinations.map((section) => [section.id, section])
  );
  const buildStructuredLandingItem = (sectionId) => {
    const section = careSetupDestinationsById.get(sectionId);
    if (!section) return null;
    return {
      id: section.id,
      icon: SECTION_ICON_BY_ID[section.id],
      label: section.label,
      description: section.description,
      tone: section.tone,
      onClick: () => {
        if (onSectionSelect) {
          onSectionSelect(section);
          return;
        }
        onStepChange?.(section.targetStep);
      },
    };
  };
  const activeProviders = React.useMemo(() => (
    Array.isArray(providers)
      ? providers.filter((provider) => provider?.isActive !== false)
      : []
  ), [providers]);
  const providerPreview = activeProviders
    .slice(0, 3)
    .map((provider) => [provider?.name, provider?.specialty].filter(Boolean).join(' · '))
    .filter(Boolean)
    .join(', ');
  const providerSummary = `${activeProviders.length} active provider${activeProviders.length === 1 ? '' : 's'}`;
  const providerLandingItem = {
    id: 'providers',
    icon: LocalHospitalOutlinedIcon,
    label: 'Care Providers',
    description: providerPreview
      ? `${providerSummary} · ${providerPreview}`
      : `${providerSummary}. Manage clinicians and other care providers in one place.`,
    tone: 'medical',
    onClick: onOpenProviders ? () => onOpenProviders() : undefined,
  };
  const chapterLandingItems = new Map(
    HANDBOOK_CHAPTERS.filter((entry) => entry.id !== "glance").map((entry) => [entry.id, {
      id: entry.id,
      icon: entry.icon,
      label: chapterPropsById[entry.id]?.chapter?.title || entry.fallbackTitle,
      description: chapterPropsById[entry.id]?.chapter?.description
        || chapterPropsById[entry.id]?.chapter?.caregiverQuestion
        || getHandbookChapterStatusLabel(chapterPropsById[entry.id]?.chapter),
      tone: "communication",
      onClick: () => {
        setActiveLandingView("handbook");
        setActiveHandbookChapterId(entry.id);
        setActiveHandbookChapterQuickEntry(true);
      },
    }])
  );
  const profileLandingItems = [
    buildStructuredLandingItem(CARE_PROFILE_SECTION_IDS.ABOUT),
    chapterLandingItems.get("communication"),
    chapterLandingItems.get("whatHelps"),
    chapterLandingItems.get("watchFor"),
    chapterLandingItems.get("dailyLife"),
    chapterLandingItems.get("safety"),
    chapterLandingItems.get("healthCare"),
    providerLandingItem,
    buildStructuredLandingItem(CARE_PROFILE_SECTION_IDS.MEDICATION),
    buildStructuredLandingItem(CARE_PROFILE_SECTION_IDS.ALLERGIES_FOOD),
    chapterLandingItems.get("goals"),
  ].filter(Boolean);
  const landingNeedToKnowItems = buildProfileLandingNeedToKnowItems({
    existingItems: profileOverview?.needToKnowItems || [],
    communicationChapter,
    whatHelpsChapter,
    watchForChapter,
    safetyChapter,
  });

  const recentUpdateItems = [...knowledgeFacts]
    .filter((fact) => fact?.updatedAt || fact?.capturedAt)
    .sort((a, b) => new Date(b.updatedAt || b.capturedAt) - new Date(a.updatedAt || a.capturedAt))
    .slice(0, 3)
    .map((fact) => ({
      id: fact.id || fact.title,
      icon: DescriptionOutlinedIcon,
      tone: "support",
      title: fact.title || "Care update",
      subtitle: fact.summary || "",
      timeLabel: formatUpdateTimeLabel(fact.updatedAt || fact.capturedAt),
    }));

  return (
    <Stack spacing={1.6}>
      <ProfileHero
        child={child}
        childAccent={childAccent}
        displayName={displayName}
        age={age}
        photoURL={photoURL}
        conditionLabel={conditionLabel}
        preferredLanguage={preferredLanguage}
        profileSetupStatus={resolvedProfileSetupStatus}
        showProgress={!isStarterOnlyIntake && mode !== "edit"}
        onEdit={isLanding ? handleEditHeader : undefined}
      />
      {mode === "add" && stage === "setup" ? (
        <StarterLoggingHandoff displayName={displayName} />
      ) : null}
      {!isStarterOnlyIntake && mode !== "edit" ? (
        <>
          <Box component="section" aria-label="Care Story">
            <StoryPreviewCard
              story={profileStory}
              profileSetupStatus={resolvedProfileSetupStatus}
              onReadFullStory={openStoryView}
              onShare={() => setIsShareOpen(true)}
            />
          </Box>
          <Box component="section" aria-labelledby="caregiver-handbook-heading">
            <Stack spacing={1.15}>
              <CaregiverHandbookHeading />
              <ProfileCommunicationChapter
                chapter={communicationChapter}
                status={communicationChapterStatus}
                knowledgeFacts={knowledgeFacts}
                canEditProfile={canEditProfile}
                canViewDocuments={canViewDocuments}
                onAddCta={canEditProfile ? () => openAddSheetForChapter("communication") : undefined}
                addCtaLabel={CHAPTER_ADD_CTA.communication.label}
              />
              <ProfileWhatHelpsChapter
                chapter={whatHelpsChapter}
                status={whatHelpsChapterStatus}
                knowledgeFacts={knowledgeFacts}
                canEditProfile={canEditProfile}
                canViewDocuments={canViewDocuments}
                onAddCta={canEditProfile ? () => openAddSheetForChapter("whatHelps") : undefined}
                addCtaLabel={CHAPTER_ADD_CTA.whatHelps.label}
              />
              <ProfileWatchForChapter
                chapter={watchForChapter}
                status={watchForChapterStatus}
                knowledgeFacts={knowledgeFacts}
                canEditProfile={canEditProfile}
                canViewDocuments={canViewDocuments}
                onAddCta={canEditProfile ? () => openAddSheetForChapter("watchFor") : undefined}
                addCtaLabel={CHAPTER_ADD_CTA.watchFor.label}
              />
              <ProfileDailyLifeChapter
                chapter={dailyLifeChapter}
                status={dailyLifeChapterStatus}
                knowledgeFacts={knowledgeFacts}
                canEditProfile={canEditProfile}
                canViewDocuments={canViewDocuments}
                onAddCta={canEditProfile ? () => openAddSheetForChapter("dailyLife") : undefined}
                addCtaLabel={CHAPTER_ADD_CTA.dailyLife.label}
              />
              <ProfileSafetyChapter
                chapter={safetyChapter}
                status={safetyChapterStatus}
                knowledgeFacts={knowledgeFacts}
                canEditProfile={canEditProfile}
                canViewDocuments={canViewDocuments}
                onAddCta={canEditProfile ? () => openAddSheetForChapter("safety") : undefined}
                addCtaLabel={CHAPTER_ADD_CTA.safety.label}
              />
              <ProfileHealthCareChapter
                chapter={healthCareChapter}
                status={healthCareChapterStatus}
                knowledgeFacts={knowledgeFacts}
                canEditProfile={canEditProfile}
                canViewDocuments={canViewDocuments}
                onNavigateToManager={handleNavigateToManager}
                onAddCta={canEditProfile ? () => openAddSheetForChapter("healthCare") : undefined}
                addCtaLabel={CHAPTER_ADD_CTA.healthCare.label}
              />
              <ProfileGoalsChapter
                chapter={resolvedGoalsChapter}
                status={goalsChapterStatus}
                onEntryAction={canEditProfile ? openGoal : undefined}
                onAddCta={canEditProfile ? () => openAddSheetForChapter("goals") : undefined}
                addCtaLabel={CHAPTER_ADD_CTA.goals.label}
              />
            </Stack>
          </Box>
          <CareSetupSection
            sections={profileSections}
            personName={displayName}
            onSectionSelect={onSectionSelect}
            onStepChange={onStepChange}
            onOpenCareTeam={onOpenCareTeam}
            onOpenCollectedInformation={onOpenCollectedInformation}
          />
          {legacyProfileSections.length ? (
            <Box component="section" aria-labelledby="additional-profile-sections-heading">
              <Stack spacing={1}>
                <TransitionalProfileSetupHeading />
                <CareProfileSectionNavigator
                  sections={legacyProfileSections}
                  completionSummary={legacyCompletionSummary}
                  onSectionSelect={onSectionSelect}
                  onStepChange={onStepChange}
                  onOpenCareTeam={onOpenCareTeam}
                />
              </Stack>
            </Box>
          ) : null}
          {canEditProfile && legacyProfileSections.length ? (
            <NextSectionCard
              guidance={{
                ...guidance,
                actions: (guidance?.actions || []).filter(
                  (action) => !CARE_SETUP_SECTION_IDS.includes(action.id)
                ),
              }}
              onSectionSelect={onSectionSelect}
              onStepChange={onStepChange}
            />
          ) : null}
          {sectionGroups.map((group) => (
            <SectionGroup
              key={group.title}
              title={group.title}
              sections={group.sections}
              onSectionSelect={onSectionSelect}
              onStepChange={onStepChange}
              onOpenCareTeam={onOpenCareTeam}
            />
          ))}
        </>
      ) : null}
      {isLanding ? (
        <Stack spacing={1.4} component="section" aria-label="Profile sections">
          <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", width: "100%" }}>
            <Button
              variant="contained"
              aria-label={`Read ${displayName}'s Story`}
              startIcon={<AutoAwesomeRoundedIcon sx={{ fontSize: { xs: 17, sm: 20 } }} />}
              onClick={openStoryView}
              sx={{
                flex: { xs: 1, sm: "0 0 auto" },
                minWidth: 0,
                borderRadius: 999,
                px: { xs: 1.15, sm: 1.75 },
                py: { xs: 0.75, sm: 0.85 },
                textTransform: "none",
                whiteSpace: "nowrap",
                fontSize: { xs: "0.8rem", sm: "0.9rem" },
                fontWeight: 500,
                bgcolor: colors.brand.deep,
                color: colors.semantic.profileFlow.cardBg,
                "& .MuiButton-startIcon": { mr: { xs: 0.55, sm: 0.8 } },
                "&:hover": { bgcolor: alpha(colors.brand.deep, 0.92) },
              }}
            >
              Read Story
            </Button>
            <IconButton
              aria-label={`Share ${displayName}'s Profile`}
              onClick={() => setIsShareOpen(true)}
              sx={{
                width: { xs: 38, sm: 42 },
                height: { xs: 38, sm: 42 },
                flexShrink: 0,
                border: `1px solid ${alpha(colors.brand.deep, 0.5)}`,
                color: colors.brand.deep,
                bgcolor: colors.semantic.profileFlow.cardBg,
                "&:hover": { bgcolor: alpha(colors.brand.deep, 0.08) },
              }}
            >
              <IosShareRoundedIcon sx={{ fontSize: { xs: 19, sm: 21 } }} />
            </IconButton>
          </Stack>
          {onOpenCollectedInformation ? (
            <CollectedInformationCard
              personName={displayName}
              onClick={onOpenCollectedInformation}
            />
          ) : null}
          <CareEvidenceReviewPanel childId={child?.id || ""} />
          <ProfileReviewQueue childId={child?.id || ""} personName={displayName} canResolve={canEditProfile} />
          {onOpenPlans ? (
            <LandingNavCard
              icon={PlaylistAddCheckRoundedIcon}
              label="Plans & next steps"
              description={`View active plans and next steps for ${displayName}.`}
              tone="goals"
              onClick={onOpenPlans}
            />
          ) : null}
          <QuickAccessRow items={profileLandingItems} />
          <NeedToKnowCard items={landingNeedToKnowItems} />
          <Stack spacing={0.7} component="nav" aria-label="Profile sections">
            {profileLandingItems.map((item) => (
              <LandingNavCard
                key={item.id}
                icon={item.icon}
                label={item.label}
                description={item.description}
                tone={item.tone}
                onClick={item.onClick}
              />
            ))}
          </Stack>
          <RecentUpdatesSection
            items={recentUpdateItems}
            onViewAll={recentUpdateItems.length ? () => setActiveLandingView("handbook") : undefined}
          />
        </Stack>
      ) : null}
      {!isLanding && mode === "edit" && activeLandingView === "story" ? (
        <Stack spacing={1.15} component="section" aria-label="Care Story">
          <CareProfileStoryReview
            story={profileStory}
            canEditProfile={false}
            mode="read"
            showEmptySections={false}
          />
        </Stack>
      ) : null}
      {!isLanding && mode === "edit" && activeLandingView === "handbook" && !activeHandbookChapterMeta ? (
        <Stack spacing={1.15} component="section" aria-labelledby="caregiver-handbook-heading">
          <CaregiverHandbookHeading personName={displayName} />
          <Button
            variant="outlined"
            startIcon={<IosShareRoundedIcon />}
            onClick={() => setIsShareOpen(true)}
            sx={{
              alignSelf: "flex-start",
              borderRadius: 999,
              px: 2,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Share Care Guide
          </Button>
          <Stack spacing={0.9} component="nav" aria-label="Handbook chapters">
            {HANDBOOK_CHAPTERS.map((entry) => {
              const chapterInfo = chapterPropsById[entry.id];
              const title = entry.id === "glance"
                ? entry.fallbackTitle
                : (chapterInfo?.chapter?.title || entry.fallbackTitle);
              const description = entry.id === "glance"
                ? getCareStoryStatus(resolvedProfileSetupStatus).label
                : getHandbookChapterStatusLabel(chapterInfo?.chapter);
              return (
                <LandingNavCard
                  key={entry.id}
                  icon={entry.icon}
                  label={title}
                  description={description}
                  tone="communication"
                  onClick={() => {
                    setActiveHandbookChapterQuickEntry(false);
                    setActiveHandbookChapterId(entry.id);
                  }}
                />
              );
            })}
          </Stack>
        </Stack>
      ) : null}
      {!isLanding && mode === "edit" && activeLandingView === "handbook" && activeHandbookChapterMeta ? (
        <Stack spacing={1.15} component="section" aria-label={activeHandbookChapterMeta.fallbackTitle}>
          {activeHandbookChapterMeta.id === "glance" ? (
            <StoryPreviewCard
              story={profileStory}
              profileSetupStatus={resolvedProfileSetupStatus}
              variant="atAGlance"
              onShare={() => setIsShareOpen(true)}
            />
          ) : (
            (() => {
              const { chapter, status, Component, extraProps } = chapterPropsById[activeHandbookChapterMeta.id];
              return <Component chapter={chapter} status={status} {...(extraProps || {})} />;
            })()
          )}
        </Stack>
      ) : null}
      {!isLanding && mode === "edit" && activeLandingView === "careSetup" ? (
        <Stack spacing={1.15} component="section" aria-label="Care Setup">
          <CareSetupSection
            sections={profileSections}
            personName={displayName}
            onSectionSelect={onSectionSelect}
            onStepChange={onStepChange}
            onOpenCareTeam={onOpenCareTeam}
            onOpenCollectedInformation={onOpenCollectedInformation}
          />
        </Stack>
      ) : null}
      {isShareOpen ? (
        <UnifiedProfileShareFlow
          open
          onClose={() => setIsShareOpen(false)}
          childId={child?.id}
          personName={displayName}
          age={age}
          preferredLanguage={preferredLanguage}
          selectedConditions={selectedConditions}
          chapters={shareChapters}
          medications={medications}
          providers={providers}
          foodAllergies={foodAllergies}
          dietaryRestrictions={dietaryRestrictions}
          documents={documents}
          canViewDocuments={canViewDocuments}
        />
      ) : null}
      {mode === "add" ? (
        <AddModeActions
          stage={stage}
          isSubmitting={isSubmitting}
          onCreate={onCreate}
          onFinish={onFinish}
        />
      ) : null}
      {canEditProfile ? (
        <UniversalProfileAddSheet
          open={isAddSheetOpen}
          onClose={() => { setIsAddSheetOpen(false); setAddSheetContext({}); }}
          onRouteIntent={onAddIntent}
          childId={child?.id || ""}
          childName={displayName}
          accent={childAccent}
          initialIntent={addSheetContext.initialIntent || ""}
          captureTitle={addSheetContext.captureTitle}
          capturePlaceholder={addSheetContext.capturePlaceholder}
          profileChapterId={addSheetContext.profileChapterId || ""}
          profileChapterName={addSheetContext.chapterName || ""}
          profileField={addSheetContext.profileField || ""}
          profilePlacementMode={addSheetContext.profilePlacementMode || ""}
        />
      ) : null}
      <ProfileGoalSheet
        open={Boolean(selectedGoal)}
        goal={selectedGoal}
        child={childForGoalWrites}
        onClose={closeGoal}
        onChanged={applySupportProfile}
        onRemoved={handleRemoved}
      />
      <Snackbar
        open={Boolean(goalMessage)}
        autoHideDuration={canUndoRemove ? 6000 : 3000}
        onClose={clearGoalMessage}
        message={goalMessage}
        action={canUndoRemove ? (
          <Button color="inherit" size="small" onClick={undoRemove} sx={{ fontWeight: 900 }}>
            Undo
          </Button>
        ) : undefined}
      />
    </Stack>
  );
};

export default CareProfileOverview;
